import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { Badge } from '../components/Badge';
import { Avatar, AvatarFallback } from '../components/Avatar';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../components/Sheet';
import { ScrollArea } from '../components/ScrollArea';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { Search, Filter, PoundSterling, FileSearch, Target, AlertTriangle, CheckCircle2, Bot, Check, Edit, FileText, History, ShieldAlert } from 'lucide-react';
const kpiData = [{
  title: 'Episodes Audited',
  value: '1,247',
  sub: 'Last 30 days · continuous',
  icon: FileSearch,
  color: '#3B82F6',
  data: [820, 870, 905, 950, 1010, 1080, 1150, 1247]
}, {
  title: 'Under-coded Cases Flagged',
  value: '87',
  sub: '7.0% of audited episodes',
  icon: AlertTriangle,
  color: '#C8892A',
  data: [12, 28, 41, 53, 64, 72, 80, 87]
}, {
  title: 'Estimated Tariff Recovery',
  value: '£142,380',
  sub: 'Pending coder review',
  icon: PoundSterling,
  color: '#22C55E',
  data: [18000, 36000, 58000, 79000, 95000, 112000, 128000, 142380]
}, {
  title: 'Coding Accuracy Rate',
  value: '93.0%',
  sub: 'vs. 91.4% prior quarter',
  icon: Target,
  color: '#1C3A2F',
  data: [91.0, 91.4, 91.8, 92.1, 92.4, 92.6, 92.8, 93.0]
}];
const mockCases = [{
  id: '1',
  patient: 'William George',
  initials: 'WG',
  procedure: 'Primary THR (R)',
  currentCodes: ['W37.1', 'M16.1'],
  proposedCodes: ['W37.1', 'M16.1', 'Y70.1'],
  addition: 'Y70.1 — Intraoperative blood loss >1L',
  delta: '+£1,840',
  confidence: 94,
  status: 'Flagged',
  framework: 'HRG: HN12C → HN12B',
  date: '12/03/2026',
  evidence: 'Op note records estimated blood loss of 1,200ml. Anaesthetic chart confirms 2 units RBC transfused intraoperatively. Current coding omits Y70.1 supplementary code.',
  rationale: 'Adding Y70.1 reclassifies the spell from HRG HN12C to HN12B, reflecting the documented intraoperative complexity.'
}, {
  id: '2',
  patient: 'Robert Miller',
  initials: 'RM',
  procedure: 'Hemiarthroplasty (NOF)',
  currentCodes: ['W46.1', 'S72.0'],
  proposedCodes: ['W46.1', 'S72.0', 'I50.9', 'N17.9'],
  addition: 'I50.9 + N17.9 — comorbidity complexity split',
  delta: '+£2,210',
  confidence: 91,
  status: 'Flagged',
  framework: 'HRG: HD23E → HD23C',
  date: '11/03/2026',
  evidence: 'Admission clerking documents NYHA III heart failure on furosemide 80mg. Day 2 bloods show AKI stage 1 (creatinine rise 156→212). Neither secondary diagnosis captured in current coding.',
  rationale: 'Adding I50.9 (heart failure) and N17.9 (AKI) triggers comorbidity-split HRG, materially increasing the tariff for this complex NOF spell.'
}, {
  id: '3',
  patient: 'Patricia Taylor',
  initials: 'PT',
  procedure: 'Lumbar Decompression L4/5',
  currentCodes: ['V25.4', 'M48.06'],
  proposedCodes: ['V25.4', 'M48.06', 'V67.3'],
  addition: 'V67.3 — Posterior instrumentation',
  delta: '+£1,560',
  confidence: 96,
  status: 'Flagged',
  framework: 'HRG: HC32D → HC32B',
  date: '10/03/2026',
  evidence: 'Op note describes "pedicle screws inserted L4 and L5 bilaterally with rod construct." Instrumentation present but not coded.',
  rationale: 'V67.3 captures the spinal instrumentation procedure and corrects the HRG to reflect the actual operative complexity.'
}, {
  id: '4',
  patient: 'James Brown',
  initials: 'JB',
  procedure: 'Primary TKR (L)',
  currentCodes: ['W40.1', 'M17.1'],
  proposedCodes: ['W40.1', 'M17.1'],
  addition: '—',
  delta: '—',
  confidence: 99,
  status: 'Verified',
  framework: 'HRG: HN22B (correct)',
  date: '09/03/2026',
  evidence: 'Coding consistent with operative findings and discharge summary. No additional codes warranted.',
  rationale: 'No change recommended. Spell coded correctly first pass.'
}, {
  id: '5',
  patient: 'Mary Williams',
  initials: 'MW',
  procedure: 'Knee Arthroscopy (R)',
  currentCodes: ['W82.1'],
  proposedCodes: ['W82.1', 'Z96.6'],
  addition: 'Z96.6 — Presence of joint prosthesis',
  delta: '+£420',
  confidence: 88,
  status: 'Flagged',
  framework: 'HRG: HN43C → HN43B',
  date: '08/03/2026',
  evidence: 'PMH records prior ipsilateral TKR (2019). Z-code for prosthesis presence not applied.',
  rationale: 'Z96.6 captures relevant device history and adjusts HRG complexity for the revision arthroscopy context.'
}, {
  id: '6',
  patient: 'Linda Davis',
  initials: 'LD',
  procedure: 'Ankle ORIF',
  currentCodes: ['W19.1', 'S82.6'],
  proposedCodes: ['W19.1', 'S82.6', 'W28.1'],
  addition: 'W28.1 — Open reduction with internal fixation',
  delta: '+£780',
  confidence: 92,
  status: 'Flagged',
  framework: 'HRG: HT13C → HT13B',
  date: '07/03/2026',
  evidence: 'Op note documents fibular plate with five cortical screws plus syndesmotic screw. Fixation method not separately coded.',
  rationale: 'W28.1 captures the internal fixation component, distinct from the primary fracture reduction code.'
}, {
  id: '7',
  patient: 'Michael Wilson',
  initials: 'MW',
  procedure: 'Revision THR (L)',
  currentCodes: ['W38.1', 'T84.5'],
  proposedCodes: ['W38.1', 'T84.5', 'B95.6'],
  addition: 'B95.6 — Staph aureus as cause',
  delta: '+£1,180',
  confidence: 90,
  status: 'Flagged',
  framework: 'HRG: HN15B → HN15A',
  date: '06/03/2026',
  evidence: 'Two intraoperative tissue samples positive for MSSA. Causative organism code absent from current coding.',
  rationale: 'B95.6 specifies the infecting organism, refining the prosthetic joint infection coding to the higher-complexity HRG band.'
}];
const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'Flagged':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'Verified':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'Approved':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    default:
      return '';
  }
};
const getStatusIcon = (status: string) => {
  switch (status) {
    case 'Flagged':
      return <AlertTriangle className="w-4 h-4 text-amber-600" data-id="element-308" />;
    case 'Verified':
      return <CheckCircle2 className="w-4 h-4 text-green-600" data-id="element-309" />;
    case 'Approved':
      return <CheckCircle2 className="w-4 h-4 text-blue-600" data-id="element-310" />;
    default:
      return null;
  }
};
export function CodingAudit() {
  const [selectedCase, setSelectedCase] = useState<(typeof mockCases)[0] | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const handleRowClick = (c: (typeof mockCases)[0]) => {
    setSelectedCase(c);
    setIsSheetOpen(true);
  };
  return <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-4" data-id="element-311">
      <div className="flex justify-between items-start" data-id="element-312">
        <div data-id="element-313">
          <div className="flex items-center gap-3 mb-1" data-id="element-314">
            <h1 className="font-heading text-2xl sm:text-3xl font-normal text-foreground" data-id="element-315">
              Coding Audit
            </h1>
            <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 font-medium" data-id="element-316">
              Operational
            </Badge>
          </div>
          <p className="text-muted-foreground text-sm" data-id="element-317">
            Retrospective audit of ICD-10 and OPCS-4 assignment against the
            clinical record
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border" data-id="element-318">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-319" />
          <span className="text-xs font-medium text-foreground" data-id="element-320">
            Audit agent live
          </span>
          <span className="text-xs text-muted-foreground" data-id="element-321">· Continuous</span>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-322">
        {kpiData.map((kpi, i) => <Card key={i} className="overflow-hidden" data-id="element-323">
            <CardContent className="p-6" data-id="element-324">
              <div className="flex justify-between items-start mb-4" data-id="element-325">
                <span className="text-sm font-medium text-muted-foreground" data-id="element-326">
                  {kpi.title}
                </span>
                <div className="p-2 rounded-full bg-secondary" data-id="element-327">
                  <kpi.icon className="w-4 h-4 text-foreground" data-id="element-328" />
                </div>
              </div>
              <div className="flex items-end justify-between" data-id="element-329">
                <div data-id="element-330">
                  <div className="font-heading text-4xl font-normal text-foreground" data-id="element-331">
                    {kpi.value}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1" data-id="element-332">
                    {kpi.sub}
                  </div>
                </div>
                <div className="h-12 w-24" data-id="element-333">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-334">
                    <LineChart data={kpi.data.map((val, idx) => ({
                  val,
                  idx
                }))} data-id="element-335">
                      <Line type="monotone" dataKey="val" stroke={kpi.color} strokeWidth={2} dot={false} isAnimationActive={false} data-id="element-336" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>)}
      </div>

      {/* Search and filter */}
      <div className="flex justify-between items-center gap-4" data-id="element-337">
        <div className="relative flex-1 max-w-md" data-id="element-338">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" data-id="element-339" />
          <Input placeholder="Search by patient, procedure, code..." className="pl-9 bg-card" data-id="element-340" />
        </div>
        <div className="flex items-center gap-2" data-id="element-341">
          <Badge variant="outline" className="bg-card" data-id="element-342">
            Specialty: Trauma & Orthopaedics
          </Badge>
          <Button variant="outline" className="gap-2 bg-card" data-id="element-343">
            <Filter className="w-4 h-4" data-id="element-344" />
            Filter
          </Button>
        </div>
      </div>

      {/* Cases table */}
      <Card data-id="element-345">
        <Table data-id="element-346">
          <TableHeader data-id="element-347">
            <TableRow data-id="element-348">
              <TableHead data-id="element-349">Patient</TableHead>
              <TableHead data-id="element-350">Procedure</TableHead>
              <TableHead data-id="element-351">Current Coding</TableHead>
              <TableHead data-id="element-352">Proposed Addition</TableHead>
              <TableHead className="text-right" data-id="element-353">Tariff Δ</TableHead>
              <TableHead data-id="element-354">Confidence</TableHead>
              <TableHead data-id="element-355">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-id="element-356">
            {mockCases.map(row => <TableRow key={row.id} className="cursor-pointer hover:bg-secondary/50" onClick={() => handleRowClick(row)} data-id="element-357">
                <TableCell className="font-medium" data-id="element-358">
                  <div className="flex items-center gap-3" data-id="element-359">
                    <Avatar className="h-8 w-8 border border-border" data-id="element-360">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs" data-id="element-361">
                        {row.initials}
                      </AvatarFallback>
                    </Avatar>
                    {row.patient}
                  </div>
                </TableCell>
                <TableCell className="text-sm" data-id="element-362">{row.procedure}</TableCell>
                <TableCell data-id="element-363">
                  <div className="flex gap-1 flex-wrap" data-id="element-364">
                    {row.currentCodes.map(c => <Badge key={c} variant="outline" className="font-mono text-xs bg-card" data-id="element-365">
                        {c}
                      </Badge>)}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground" data-id="element-366">
                  {row.addition}
                </TableCell>
                <TableCell className={`text-right font-mono text-sm ${row.delta !== '—' ? 'text-green-700 font-bold' : 'text-muted-foreground'}`} data-id="element-367">
                  {row.delta}
                </TableCell>
                <TableCell data-id="element-368">
                  {row.status === 'Verified' ? <span className="text-xs text-muted-foreground" data-id="element-369">—</span> : <div className="flex items-center gap-2" data-id="element-370">
                      <div className="w-16 h-1.5 bg-secondary rounded-full overflow-hidden" data-id="element-371">
                        <div className="h-full bg-primary" style={{
                    width: `${row.confidence}%`
                  }} data-id="element-372" />
                      </div>
                      <span className="text-xs font-mono text-muted-foreground" data-id="element-373">
                        {row.confidence}%
                      </span>
                    </div>}
                </TableCell>
                <TableCell data-id="element-374">
                  <div className="flex items-center gap-2" data-id="element-375">
                    {getStatusIcon(row.status)}
                    <Badge variant="outline" className={getStatusBadgeClass(row.status)} data-id="element-376">
                      {row.status}
                    </Badge>
                  </div>
                </TableCell>
              </TableRow>)}
          </TableBody>
        </Table>
      </Card>

      {/* Detail sheet */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen} data-id="element-377">
        <SheetContent side="right" className="w-[640px] sm:max-w-none p-0 flex flex-col bg-background" data-id="element-378">
          {selectedCase && <>
              <div className="p-6 border-b border-border bg-card" data-id="element-379">
                <div className="flex justify-between items-start mb-4" data-id="element-380">
                  <div data-id="element-381">
                    <SheetTitle className="font-heading text-2xl mb-1" data-id="element-382">
                      Case Detail: {selectedCase.patient}
                    </SheetTitle>
                    <SheetDescription className="flex items-center gap-2" data-id="element-383">
                      <span data-id="element-384">{selectedCase.procedure}</span>
                      <span data-id="element-385">•</span>
                      <span data-id="element-386">Discharged: {selectedCase.date}</span>
                    </SheetDescription>
                  </div>
                  <Badge variant="outline" className={`text-sm px-3 py-1 ${getStatusBadgeClass(selectedCase.status)}`} data-id="element-387">
                    {selectedCase.status}
                  </Badge>
                </div>
              </div>

              <ScrollArea className="flex-1 p-6" data-id="element-388">
                <div className="space-y-8" data-id="element-389">
                  {/* HRG impact */}
                  <div className="space-y-3" data-id="element-390">
                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-391">
                      <PoundSterling className="w-4 h-4 text-muted-foreground" data-id="element-392" />
                      HRG & Tariff Impact
                    </h3>
                    <Card className="bg-secondary/30" data-id="element-393">
                      <CardContent className="p-4 space-y-2" data-id="element-394">
                        <div className="flex justify-between items-center text-sm" data-id="element-395">
                          <span className="text-muted-foreground" data-id="element-396">
                            Mapping:
                          </span>
                          <span className="font-mono text-xs text-foreground" data-id="element-397">
                            {selectedCase.framework}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-id="element-398">
                          <span className="text-muted-foreground" data-id="element-399">
                            Estimated tariff recovery:
                          </span>
                          <span className={`font-mono font-bold ${selectedCase.delta !== '—' ? 'text-green-700' : 'text-muted-foreground'}`} data-id="element-400">
                            {selectedCase.delta}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Code comparison */}
                  <div className="space-y-3" data-id="element-401">
                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-402">
                      <FileText className="w-4 h-4 text-muted-foreground" data-id="element-403" />
                      Coding Comparison
                    </h3>
                    <div className="grid grid-cols-2 gap-3" data-id="element-404">
                      <Card data-id="element-405">
                        <CardContent className="p-4 space-y-3" data-id="element-406">
                          <p className="text-xs font-bold text-muted-foreground uppercase" data-id="element-407">
                            Current
                          </p>
                          <div className="flex gap-1 flex-wrap" data-id="element-408">
                            {selectedCase.currentCodes.map(c => <Badge key={c} variant="outline" className="font-mono text-xs bg-card" data-id="element-409">
                                {c}
                              </Badge>)}
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="border-primary/30 shadow-sm bg-primary/5" data-id="element-410">
                        <CardContent className="p-4 space-y-3" data-id="element-411">
                          <p className="text-xs font-bold text-primary uppercase" data-id="element-412">
                            Agent Proposed
                          </p>
                          <div className="flex gap-1 flex-wrap" data-id="element-413">
                            {selectedCase.proposedCodes.map(c => {
                          const isNew = !selectedCase.currentCodes.includes(c);
                          return <Badge key={c} variant="outline" className={`font-mono text-xs ${isNew ? 'bg-green-100 text-green-800 border-green-300' : 'bg-card'}`} data-id="element-414">
                                  {c}
                                  {isNew && <span className="ml-1 font-sans" data-id="element-415">+</span>}
                                </Badge>;
                        })}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  {/* Clinical evidence */}
                  {selectedCase.status === 'Flagged' && <div className="space-y-3" data-id="element-416">
                      <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-417">
                        <Bot className="w-4 h-4 text-primary" data-id="element-418" />
                        Audit Agent Rationale
                      </h3>
                      <Card className="border-primary/20 shadow-sm" data-id="element-419">
                        <CardContent className="p-5 space-y-4" data-id="element-420">
                          <div data-id="element-421">
                            <p className="text-xs font-bold text-muted-foreground uppercase mb-2" data-id="element-422">
                              Clinical Evidence
                            </p>
                            <p className="text-sm text-foreground italic border-l-2 border-primary/30 pl-3 py-1" data-id="element-423">
                              "{selectedCase.evidence}"
                            </p>
                          </div>
                          <div data-id="element-424">
                            <p className="text-xs font-bold text-muted-foreground uppercase mb-2" data-id="element-425">
                              Coding Rationale
                            </p>
                            <p className="text-sm text-foreground leading-relaxed" data-id="element-426">
                              {selectedCase.rationale}
                            </p>
                          </div>
                          <div className="flex gap-3 pt-4 border-t border-border" data-id="element-427">
                            <Button className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground gap-2" data-id="element-428">
                              <Check className="w-4 h-4" data-id="element-429" />
                              Approve Re-coding
                            </Button>
                            <Button variant="outline" className="flex-1 gap-2" data-id="element-430">
                              <Edit className="w-4 h-4" data-id="element-431" />
                              Send to Coder
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>}

                  {/* Activity timeline */}
                  <div className="space-y-3" data-id="element-432">
                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-433">
                      <History className="w-4 h-4 text-muted-foreground" data-id="element-434" />
                      Audit Trail
                    </h3>
                    <div className="pl-2 space-y-4 border-l-2 border-border ml-2" data-id="element-435">
                      {selectedCase.status === 'Flagged' && <div className="relative pl-6" data-id="element-436">
                          <div className="absolute -left-[21px] top-1 w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center border-4 border-background" data-id="element-437">
                            <ShieldAlert className="w-2 h-2 text-white" data-id="element-438" />
                          </div>
                          <p className="text-sm font-medium text-foreground" data-id="element-439">
                            Under-coding flagged for review
                          </p>
                          <p className="text-xs text-muted-foreground" data-id="element-440">
                            Today at 08:14 AM · Confidence{' '}
                            {selectedCase.confidence}%
                          </p>
                        </div>}
                      <div className="relative pl-6" data-id="element-441">
                        <div className="absolute -left-[21px] top-1 w-4 h-4 rounded-full bg-primary flex items-center justify-center border-4 border-background" data-id="element-442">
                          <Bot className="w-2 h-2 text-primary-foreground" data-id="element-443" />
                        </div>
                        <p className="text-sm font-medium text-foreground" data-id="element-444">
                          Audit agent reviewed episode
                        </p>
                        <p className="text-xs text-muted-foreground" data-id="element-445">
                          Today at 08:13 AM · Sources: op note, anaesthetic
                          chart, discharge summary
                        </p>
                      </div>
                      <div className="relative pl-6" data-id="element-446">
                        <div className="absolute -left-[21px] top-1 w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center border-4 border-background" data-id="element-447">
                          <Check className="w-2 h-2 text-white" data-id="element-448" />
                        </div>
                        <p className="text-sm font-medium text-foreground" data-id="element-449">
                          Spell coded by clinical coder
                        </p>
                        <p className="text-xs text-muted-foreground" data-id="element-450">
                          {selectedCase.date} at 11:42 AM
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </>}
        </SheetContent>
      </Sheet>
    </div>;
}